import type { EntryTitle } from '@itc-mod/library/shared';
import { ACQ_TYPES, DUPLICATE_SHIFT_MS, RECENT_LIMIT, UPCOMING_GRACE_MS } from './constants.ts';
import { NO_DEPOSIT, type AcqType, type CheckItem, type Crew, type Loadout, type Op, type Phase } from './schemas.ts';

// Sierra's rules for ops as pure functions (plan 2D R1–R5), so the server and the
// browser agree. Rows marked *port* in the plan pin divergences from the panel.

// R1: crew names (`parseRoster`, line 722; `isPlaceholder`, line 543).

/** A name that stands for nobody: blank, "Open slot" or "Unnamed member". */
export function isPlaceholder(name: string): boolean {
  const text = name.trim();
  return text === '' || /^(open slot|unnamed( member)?)$/i.test(text);
}

/** The names in pasted text: one per line, or split by commas and semicolons. Only the first column of a roster line is kept. */
export function parseCrewNames(text: string): string[] {
  const whole = text.trim();
  if (whole === '') return [];
  const lines = whole.includes('\n') ? whole.split(/\r?\n/) : whole.split(/[,;]/);
  const seen = new Set<string>();
  return lines.flatMap((line) => {
    const bare = line.replace(/^\s*(?:[-•*·>]+|\d+[.)])\s*/, '').trim();
    const [first = ''] = bare.split(/\t|\s+[-–—|]\s+/);
    const name = first.replace(/^@/, '').trim();
    const key = name.toLowerCase();
    if (isPlaceholder(name) || seen.has(key)) return [];
    seen.add(key);
    return [name];
  });
}

export interface NamedMember {
  id: string;
  displayName: string;
  username: string;
}

/** *Port:* the one active member with this display name or username, ignoring case. Several, or none, is a guest. */
export function matchMember(name: string, members: readonly NamedMember[]): string | null {
  const key = name.trim().toLowerCase();
  const found = members.filter((member) => member.displayName.toLowerCase() === key || member.username.toLowerCase() === key);
  const [only] = found;
  return found.length === 1 && only !== undefined ? only.id : null;
}

// R2: timeline times. Entries store minutes from the start; clock times are worked out in the browser.

const MINUTES_IN_DAY = 24 * 60;

/** The offset for a clock time, both in minutes since midnight. More than 6 hours before the start rolls over to the next day (`phaseMs`, line 661). */
export function offsetFromClock(startMinutes: number, clockMinutes: number): number {
  const offset = clockMinutes - startMinutes;
  return offset < -360 ? offset + MINUTES_IN_DAY : offset;
}

/** The instant of a timeline entry, or null with no start. */
export function phaseAt(start: string | null, offset: number): Date | null {
  return start === null ? null : new Date(Date.parse(start) + offset * 60_000);
}

/** *Port:* an entry with no start: "T−30", "T+150". */
export function offsetLabel(offset: number): string {
  return `T${offset < 0 ? '−' : '+'}${String(Math.abs(offset))}`;
}

export function sortedTimeline(timeline: readonly Phase[]): Phase[] {
  return [...timeline].sort((a, b) => a.offset - b.offset);
}

/** Add entry: 30 minutes after the last, or at the start if there are none. */
export function nextOffset(timeline: readonly Phase[]): number {
  const last = sortedTimeline(timeline).at(-1);
  return last === undefined ? 0 : last.offset + 30;
}

// R3: lists.

type Listed = Pick<Op, 'status' | 'start' | 'updated'>;

export type OpFilter = 'open' | 'upcoming' | 'complete' | 'all';

const startOf = (op: Pick<Op, 'start'>): number | null => (op.start === null ? null : Date.parse(op.start));

export function isUpcoming(op: Pick<Op, 'status' | 'start'>, now: Date): boolean {
  const start = startOf(op);
  return op.status !== 'complete' && start !== null && start >= now.getTime() - UPCOMING_GRACE_MS;
}

export function matchesFilter(op: Listed, filter: OpFilter, now: Date): boolean {
  if (filter === 'all') return true;
  if (filter === 'complete') return op.status === 'complete';
  if (filter === 'upcoming') return isUpcoming(op, now);
  return op.status !== 'complete';
}

/** Those with a start first, by start; then the rest, newest change first (`opsSorted`, line 660). */
export function opOrder(a: Listed, b: Listed): number {
  const [startA, startB] = [startOf(a), startOf(b)];
  if (startA !== null && startB !== null) return startA - startB;
  if (startA !== null) return -1;
  if (startB !== null) return 1;
  return Date.parse(b.updated) - Date.parse(a.updated);
}

/** An op's list, in R3's order: Complete sorts newest first. */
export function sortedOps<T extends Listed>(ops: readonly T[], filter: OpFilter, now: Date): T[] {
  const sorted = ops.filter((op) => matchesFilter(op, filter, now)).sort(opOrder);
  return filter === 'complete' ? sorted.reverse() : sorted;
}

/** The Schedule's sections (lines 1269–1272). Recent is the newest 8 past ops. */
export function scheduleSections<T extends Listed>(ops: readonly T[], now: Date): { upcoming: T[]; unscheduled: T[]; recent: T[] } {
  const started = (op: T) => startOf(op) ?? 0;
  const past = ops.filter((op) => startOf(op) !== null && (op.status === 'complete' || !isUpcoming(op, now)));
  return {
    upcoming: sortedOps(ops, 'upcoming', now),
    unscheduled: ops.filter((op) => op.status !== 'complete' && op.start === null).sort(opOrder),
    recent: past.sort((a, b) => started(b) - started(a)).slice(0, RECENT_LIMIT),
  };
}

/** Upcoming ops in order, grouped by the day a function gives each. */
export function byDay<T extends { start: string | null }>(ops: readonly T[], dayOf: (at: Date) => string): { day: string; ops: T[] }[] {
  const days: { day: string; ops: T[] }[] = [];
  for (const op of ops) {
    const day = op.start === null ? '' : dayOf(new Date(op.start));
    const last = days.at(-1);
    if (last?.day === day) last.ops.push(op);
    else days.push({ day, ops: [op] });
  }
  return days;
}

/** R17: the first of Upcoming: the Next op widget. */
export function nextOp<T extends Pick<Op, 'status' | 'start'>>(ops: readonly T[], now: Date): T | null {
  const upcoming = ops.filter((op) => isUpcoming(op, now));
  return [...upcoming].sort((a, b) => Date.parse(a.start ?? '') - Date.parse(b.start ?? ''))[0] ?? null;
}

// R4: templates.

/** What a template fills in on a new op. *Port:* the timeline is filled without a start, because entries are offsets. */
export function fromTemplate(acqType: AcqType, newId: () => string): { objective: string; plan: string; checklist: CheckItem[]; timeline: Phase[] } {
  const template = ACQ_TYPES[acqType];
  return {
    objective: template.objective,
    plan: template.plan,
    checklist: template.checklist.map((text) => ({ id: newId(), text, done: false })),
    timeline: template.phases.map(([offset, label]) => ({ id: newId(), offset, label })),
  };
}

// R5: duplicating (`dupOp`, line 1256).

const shifted = (at: string | null) => (at === null ? null : new Date(Date.parse(at) + DUPLICATE_SHIFT_MS).toISOString());

/** The copy's times, a week later. */
export function duplicateTimes(op: Pick<Op, 'start' | 'end'>): Pick<Op, 'start' | 'end'> {
  return { start: shifted(op.start), end: shifted(op.end) };
}

/** A loadout as a copy keeps it: planned, with nothing taken. */
export function plannedLoadout(loadout: Loadout): Loadout {
  return { state: 'planned', round: 0, lines: loadout.lines.map((line) => ({ ...line, taken: null })), error: '', failed: null };
}

/** The fields of a copy that differ from the op's: everything else is kept. */
/** R3's picker: every entry not yet linked, Guides & SOPs first, then by title. */
export function sopChoices(entries: readonly EntryTitle[], linked: readonly string[]): { id: string; label: string }[] {
  const rank = (entry: EntryTitle) => (entry.category === 'Guides & SOPs' ? 0 : 1);
  return entries
    .filter((entry) => !linked.includes(entry.id))
    .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title))
    .map((entry) => ({ id: entry.id, label: `${entry.category}: ${entry.title}` }));
}

export function duplicateFields(op: Op): Pick<Op, 'name' | 'status' | 'start' | 'end' | 'outcome' | 'results' | 'losses' | 'earnings' | 'deposit' | 'loadout' | 'checklist' | 'pickup' | 'usage'> {
  return {
    name: `${op.name.slice(0, 113)} (copy)`,
    status: 'planning',
    ...duplicateTimes(op),
    outcome: '',
    results: '',
    losses: '',
    earnings: 0,
    deposit: NO_DEPOSIT,
    loadout: plannedLoadout(op.loadout),
    checklist: op.checklist.map((item) => ({ ...item, done: false })),
    pickup: null,
    usage: {},
  };
}

/** Crew someone put on the op: an RSVP has no event on a copy, and a voice or Fleet row records who came, not who's planned (plan 3B R5, plan 6A). `LIMITS.crew` counts these. */
export const planned = (row: Pick<Crew, 'source'>) => row.source !== 'rsvp' && row.source !== 'voice' && row.source !== 'fleet';

/** The crew a copy keeps: the planned crew only, with the source `copied`. */
export function duplicateCrew(crew: readonly Crew[]): Pick<Crew, 'user' | 'guest'>[] {
  return crew.filter(planned).map(({ user, guest }) => ({ user, guest }));
}

// Assumption 1: deleting.

/** Why an op can't be deleted, or null. Anything settled stays on the record, and so does what's been published (plan 3C R1). */
export function deleteRefusal(op: Pick<Op, 'deposit' | 'loadout'>, stockedHaul: boolean, publishedReport: boolean): string | null {
  if (publishedReport) return 'This op has a published report: mark it Complete and Scrubbed instead';
  const settled = stockedHaul || op.deposit.state !== 'none' || op.loadout.state !== 'planned';
  return settled ? 'Something on this op has already settled: mark it Complete and Scrubbed instead' : null;
}
